import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/ui/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { TYPE_META, getRelatedVideos, getVideoById } from "@/lib/videos";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const meta = video ? TYPE_META[video.type] : null;

  usePageMeta({
    title: video ? `${video.title} - 视频详情 - 密桃视频` : "视频不存在 - 密桃视频",
    description: video
      ? `${video.title}（${meta?.label}·${video.year}）：${video.summary.slice(0, 70)}……`
      : "抱歉，该视频内容不存在或已被删除。",
    keywords: video
      ? `${video.title},${meta?.label},${video.genres.join(",")},密桃视频`
      : "密桃视频",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!video || !meta) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          该视频内容不存在或已被删除
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          抱歉，你访问的视频可能已经下架或链接有误，去其他页面看看吧。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
          >
            <i className="ri-search-line"></i>
            去搜索
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(video.id);
  const related = getRelatedVideos(video, 5);

  const handleFavorite = () => {
    const nowFavorite = toggleFavorite(video.id);
    showToast(nowFavorite ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const metaItems = [
    { label: "类型", value: `${meta.label} · ${video.genres.join(" / ")}` },
    { label: "导演", value: video.director },
    { label: "主演", value: video.actors.join("、") },
    { label: "地区", value: video.region },
    { label: "年份", value: `${video.year} 年` },
    { label: "时长", value: video.duration },
  ];

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 lg:px-8 md:py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${video.type}`} className="transition-colors hover:text-primary-600">
          {meta.label}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{video.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr] lg:gap-10">
        <div>
          <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
            <div className="aspect-video w-full">
              <img
                src={video.cover}
                alt={video.title}
                title={`${video.title} - ${meta.label} - 密桃视频`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"></div>
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-primary-500/90 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className={`${meta.icon} text-xs`}></i>
              {meta.label}
            </span>
          </div>

          <button
            type="button"
            onClick={() => showToast("当前为内容展示页面，暂不支持在线播放")}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-lg"></i>
            播放预告（演示）
          </button>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                favorited
                  ? "border-accent-300 bg-accent-100 text-accent-700"
                  : "border-background-300 text-foreground-800 hover:border-accent-300 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-share-forward-line"></i>
              分享链接
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {video.title}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 md:text-base">{video.subtitle}</p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-sm font-semibold text-background-50">
              <i className="ri-star-fill"></i>
              {video.score.toFixed(1)}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900">
              <i className="ri-time-line"></i>
              {video.status}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700">
              <i className="ri-fire-line"></i>
              热度 {video.hot}
            </span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200/70 bg-background-50 p-5 sm:grid-cols-2">
            {metaItems.map((item) => (
              <div key={item.label} className="flex gap-3 text-sm">
                <dt className="w-14 shrink-0 text-foreground-500">{item.label}</dt>
                <dd className="text-foreground-900">{item.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
              <i className="ri-file-text-line text-primary-500"></i>
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.summary}</p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {video.genres.map((genre) => (
              <span
                key={genre}
                className="rounded-md bg-accent-100 px-3 py-1 text-xs text-accent-700"
              >
                {genre}
              </span>
            ))}
          </div>

          <Link
            to={`/category/${video.type}`}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回{meta.label}频道
          </Link>
        </div>
      </div>

      <section className="mt-14" aria-label="相关推荐">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-movie-2-line"></i>
          </span>
          相关推荐
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 md:gap-4">
          {related.map((item) => (
            <VideoCard key={item.id} video={item} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-fade-in">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
            <i className="ri-checkbox-circle-line text-primary-300"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}